import { useEffect, useMemo, useState, type FC } from "react";
import { message } from "antd";

import { Modal } from "../../../shared";
import PortalViewPreview from "./PortalViewPreview";
import PortalVisibilityToggles from "./PortalVisibilityToggles";
import type { ShareableColumnId } from "../../NewAffiliate/AffiliatePamAccount/affiliatePamAccountData";
import {
  mapPortalViewConfigFromResponse,
  mapPortalViewConfigToPayload,
} from "../../../../queries/affiliates/affiliateMappers";
import { usePatchAffiliate } from "../../../../queries/affiliates/usePatchAffiliate";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import type { OperatorAffiliateDetail } from "../../../../types/affiliates";

import styles from "./EditPortalSettingsModal.module.css";

// Same code the Edit Info modal handles — friendly "no changes" toast
// instead of a red error when Save is pressed without edits.
const NO_CHANGES_ERROR_CODE = "AFFILIATE_NO_CHANGES_DETECTED";

export type EditPortalSettingsModalProps = {
  open: boolean;
  affiliate: OperatorAffiliateDetail;
  onClose: () => void;
};

const EditPortalSettingsModal: FC<EditPortalSettingsModalProps> = ({
  open,
  affiliate,
  onClose,
}) => {
  const hydratedSelection = useMemo(
    () => mapPortalViewConfigFromResponse(affiliate.portal_view_config),
    [affiliate.portal_view_config],
  );

  const [selectedColumns, setSelectedColumns] =
    useState<ShareableColumnId[]>(hydratedSelection);

  // Re-hydrate on each open so a previous edit session doesn't bleed in;
  // `destroyOnHidden` already resets the preview's tab via remount.
  useEffect(() => {
    if (open) {
      setSelectedColumns(hydratedSelection);
    }
  }, [open, hydratedSelection]);

  const patch = usePatchAffiliate();

  const handleToggleColumn = (id: ShareableColumnId) => {
    setSelectedColumns((current) =>
      current.includes(id)
        ? current.filter((columnId) => columnId !== id)
        : [...current, id],
    );
  };

  const canSave = !patch.isPending && selectedColumns.length > 0;

  const handleSubmit = async () => {
    if (!canSave) return;
    try {
      const portal_view_config = mapPortalViewConfigToPayload({
        selectedColumns,
      });
      await patch.mutateAsync({
        affiliateId: affiliate.affiliate_id,
        payload: { portal_view_config },
      });
      message.success(AppStrings.PORTAL_VIEW_UPDATED);
      onClose();
    } catch (err) {
      const envelope = (
        err as {
          response?: { data?: { error_code?: string; message?: string } };
        }
      )?.response?.data;
      if (envelope?.error_code === NO_CHANGES_ERROR_CODE) {
        message.info(AppStrings.EDIT_AFFILIATE_NO_CHANGES);
        onClose();
        return;
      }
      message.error(
        envelope?.message || AppStrings.PORTAL_VIEW_UPDATE_FAILED,
      );
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={AppStrings.EDIT_PORTAL_VIEW}
      // Preview needs room for the full KPI row + charts.
      width="90%"
      destroyOnHidden
      data-testid="edit-portal-settings-modal"
      primaryAction={{
        label: AppStrings.SAVE,
        onClick: handleSubmit,
        loading: patch.isPending,
        disabled: !canSave,
      }}
      secondaryAction={{
        label: AppStrings.CANCEL,
        onClick: onClose,
      }}
    >
      <p className={styles.subtitle}>
        {AppStrings.EDIT_PORTAL_VIEW_SUBTITLE}
      </p>

      <div className={styles.body}>
        <PortalViewPreview
          selectedColumns={selectedColumns}
          affiliateId={affiliate.affiliate_id}
          idPrefix="edit-portal-view"
          ariaLabel={AppStrings.EDIT_PORTAL_VIEW}
        />
        <PortalVisibilityToggles
          value={selectedColumns}
          onToggle={handleToggleColumn}
          idPrefix="edit-portal-view"
        />
      </div>
    </Modal>
  );
};

export default EditPortalSettingsModal;
